import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Box,
    Button,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    RecordContextProvider,
    ReferenceManyField,
    ShowBase,
    SortButton,
    TabbedShowLayout,
    useListContext,
    useRecordContext,
    useShowContext,
} from 'react-admin';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { ActivityLog } from '../activity/ActivityLog';
import { Avatar } from '../suppliercontacts/Avatar';
import { TagsList } from '../suppliercontacts/TagsList';
import { findDealLabel } from '../purchaseorders/deal';
import { Status } from '../misc/Status';
import { useConfigurationContext } from '../root/ConfigurationContext';
import { Supplier, Contact, PurchaseOrder } from '../types';
import { SupplierAside } from './SupplierAside';
import { SupplierAvatar } from './SupplierAvatar';
import { PersonErpLink } from './PersonErpLink';

export const SupplierShow = () => (
    <ShowBase>
        <SupplierShowContent />
    </ShowBase>
);

const SupplierShowContent = () => {
    const { record, isPending } = useShowContext<Supplier>();

    if (isPending || !record) return null;

    return (
        <Box mt={2} display="flex">
            <Box flex="1">
                <Card>
                    <CardContent>
                        <Box display="flex" mb={1} alignItems="center">
                            <SupplierAvatar />
                            <Box ml={2} flex="1">
                                <Typography variant="h5">
                                    {record.nome_fornec}
                                </Typography>
                                <PersonErpLink />
                            </Box>
                        </Box>

                        <TabbedShowLayout
                            sx={{
                                '& .RaTabbedShowLayout-content': { p: 0 },
                            }}
                        >
                            <TabbedShowLayout.Tab label="Atividade">
                                <ActivityLog
                                    companyId={record.id}
                                    context="company"
                                />
                            </TabbedShowLayout.Tab>
                            <TabbedShowLayout.Tab
                                label={
                                    !record.nb_contacts
                                        ? 'Nenhum Contato'
                                        : record.nb_contacts === 1
                                            ? '1 Contato'
                                            : `${record.nb_contacts} Contatos`
                                }
                                path="contacts"
                            >
                                <ReferenceManyField
                                    reference="suppliercontacts"
                                    source="id_pessoa"
                                    target="id_pessoa"
                                    sort={{ field: 'nome_contato', order: 'ASC' }}
                                    label={false}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        spacing={2}
                                        mt={1}
                                    >
                                        {!!record.nb_contacts && (
                                            <SortButton
                                                fields={[
                                                    'last_name',
                                                    'first_name',
                                                    'last_seen',
                                                ]}
                                            />
                                        )}
                                        <CreateRelatedContactButton />
                                    </Stack>
                                    <ContactsIterator />
                                </ReferenceManyField>
                            </TabbedShowLayout.Tab>
                            {record.nb_orders ? (
                                <TabbedShowLayout.Tab
                                    label={
                                        record.nb_orders === 1
                                            ? '1 OC'
                                            : `${record.nb_orders} OCs`
                                    }
                                    path="purchaseorders"
                                >
                                    <ReferenceManyField
                                        reference="purchaseorders"
                                        target="id_fornec"
                                        sort={{ field: 'nome_fornec', order: 'ASC' }}
                                    >
                                        <OCsIterator />
                                    </ReferenceManyField>
                                </TabbedShowLayout.Tab>
                            ) : null}
                        </TabbedShowLayout>
                    </CardContent>
                </Card>
            </Box>
            <SupplierAside />
        </Box>
    );
};

const ContactsIterator = () => {
    const location = useLocation();
    const { data: contacts, error, isPending } = useListContext<Contact>();

    if (isPending || error) return null;

    const now = Date.now();
    return (
        <List dense sx={{ pt: 0 }}>
            {contacts.map(contact => (
                <RecordContextProvider key={contact.id} value={contact}>
                    <ListItem
                        button
                        component={RouterLink}
                        to={`/suppliercontacts/${contact.id}/show`}
                        state={{ from: location.pathname }}
                    >
                        <ListItemAvatar>
                            <Avatar />
                        </ListItemAvatar>
                        <ListItemText
                            primary={`${contact.nome_contato}`}
                            secondary={
                                <>
                                    {contact.desc_tipo}
                                    {contact.nb_tasks
                                        ? ` - ${contact.nb_tasks} task${contact.nb_tasks > 1 ? 's' : ''
                                        }`
                                        : ''}
                                    &nbsp; &nbsp;
                                    <TagsList />
                                </>
                            }
                        />
                        {contact.data_hora && (
                            <ListItemSecondaryAction>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    component="span"
                                >
                                    última atividade{' '}
                                    {formatDistance(contact.data_hora, now, {
                                        locale: ptBR,
                                        addSuffix: true,
                                    })}{' '}
                                    <Status status={contact.temperatura} />
                                </Typography>
                            </ListItemSecondaryAction>
                        )}
                    </ListItem>
                </RecordContextProvider>
            ))}
        </List>
    );
};

const CreateRelatedContactButton = () => {
    const supplier = useRecordContext<Supplier>();
    return (
        <Button
            component={RouterLink}
            to="/suppliercontacts/create"
            state={
                supplier
                    ? {
                          record:
                              supplier.id_pessoa != null
                                  ? { id_pessoa: supplier.id_pessoa }
                                  : undefined,
                          redirectTo: `suppliers/${supplier.id}/show/contacts`,
                      }
                    : undefined
            }
            color="primary"
            size="small"
            startIcon={<PersonAddIcon />}
        >
            Adicionar contato
        </Button>
    );
};

const OCsIterator = () => {
    const { data: ocs, error, isPending } = useListContext<PurchaseOrder>();
    const { dealStages } = useConfigurationContext();
    if (isPending || error) return null;

    const now = Date.now();
    return (
        <Box>
            <List dense>
                {ocs.map(oc => (
                    <ListItem
                        button
                        key={oc.id}
                        component={RouterLink}
                        to={`/purchaseorders/${oc.id}/show`}
                    >
                        <ListItemText
                            primary={`OC ${oc.id}`}
                            secondary={
                                <>
                                    {findDealLabel(dealStages, oc.stage)},{' '}
                                    {oc.valor_total.toLocaleString('pt-BR', {
                                        notation: 'compact',
                                        style: 'currency',
                                        currency: 'BRL',
                                        currencyDisplay: 'narrowSymbol',
                                        minimumSignificantDigits: 3,
                                    })}
                                    {oc.nome_usuario ? `, ${oc.nome_usuario}` : ''}
                                </>
                            }
                        />
                        <ListItemSecondaryAction>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                component="span"
                            >
                                última atividade{' '}
                                {formatDistance(oc.data_hora, now, {
                                    locale: ptBR,
                                    addSuffix: true,
                                })}{' '}
                            </Typography>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};
